/**
 * Block Visual Registry
 *
 * Maps type to visual component.
 * This is the single source of truth for block rendering.
 */

import type { ComponentType } from 'react'
import type { PlayBlockType } from '@/types/blocks'
import type { BlockVisualProps } from './Types'

// Canvas Visuals
import { PlayerVisual } from '@/components/blocks/visuals'
import { DrawingVisual } from '@/components/blocks/visuals'
import { FormationVisual } from '@/components/blocks/visuals'
import { AnnotationVisual } from './visuals/canvas/AnnotationVisual'
import { ConceptVisual } from '@/components/blocks/visuals'
import { ConceptReferenceVisual } from '@/components/blocks/visuals'
import { ImageVisual } from './visuals/canvas/ImageVisual'
import { ShapeVisual } from './visuals/canvas/ShapeVisual'

// Document Visuals
import { DocumentVisual } from '@/components/blocks/visuals'
import { SectionVisual } from '@/components/blocks/visuals'
import { TextVisual } from './visuals/document/TextVisual'
import { HeaderVisual } from './visuals/document/HeaderVisual'
import { DividerVisual } from './visuals/document/DividerVisual'

// Layout Visuals
import { RowVisual } from './visuals/layout/RowVisual'
import { ColumnVisual } from './visuals/layout/ColumnVisual'

// Reference Visuals
import { PlayReferenceVisual } from '@/components/blocks/visuals/reference'
import { FormationReferenceVisual } from '@/components/blocks/visuals/reference'
import { DocumentReferenceVisual } from '@/components/blocks/visuals/reference'

// Placeholder for unknown block types
function UnknownBlockVisual({ block }: BlockVisualProps) {
  return (
    <div
      style={{
        padding: '1rem',
        border: '2px dashed var(--destructive)',
        borderRadius: '0.5rem',
        backgroundColor: 'color-mix(in oklch, var(--destructive) 10%, transparent)',
        color: 'var(--destructive)',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
      }}
    >
      Unknown block type: {block.type}
    </div>
  )
}

/**
 * NoRender - Used for modifier blocks that don't render themselves
 *
 * Modifier blocks (like pre_snap_marker) modify their parent's rendering
 * rather than rendering themselves directly.
 */
function NoRender() {
  return null
}

/**
 * Registry mapping type to visual component
 *
 * All PlayBlockType values must have an entry. Missing entries fall back to
 * UnknownBlockVisual at runtime via getVisualForBlockType().
 */
export const BlockVisualRegistry: Record<
  PlayBlockType,
  ComponentType<BlockVisualProps>
> = {
  // Canvas Root Blocks
  play: NoRender, // Play blocks rendered by CanvasRenderer at top level, not as children
  formation: FormationVisual,
  concept: ConceptVisual,

  // Canvas Content Blocks
  player: PlayerVisual,
  drawing: DrawingVisual,
  control_point: NoRender, // Internal to DrawingBlock - never renders independently
  // bezier_handle removed - handles embedded in control_point properties
  // path_segment removed - use block_references with reference_type='path_connection'
  annotation: AnnotationVisual,
  pre_snap_marker: NoRender, // Modifier block - modifies parent rendering, doesn't render itself
  concept_reference: ConceptReferenceVisual,

  // Document Root Blocks
  document: DocumentVisual,

  // Document Content Blocks
  section: SectionVisual,
  text: TextVisual,
  header: HeaderVisual,
  divider: DividerVisual,
  image: ImageVisual,
  shape: ShapeVisual,

  // Layout Containers
  row: RowVisual,
  column: ColumnVisual,

  // Reference Blocks
  play_reference: PlayReferenceVisual,
  formation_reference: FormationReferenceVisual,
  document_reference: DocumentReferenceVisual
}

/**
 * Get visual component for a block type
 *
 * @param type - Block type discriminator
 * @returns Visual component for this block type
 *
 * @example
 * ```tsx
 * const Visual = getVisualForBlockType('player')
 * return <Visual block={playerBlock} />
 * ```
 */
export function getVisualForBlockType(
  type: PlayBlockType
): ComponentType<BlockVisualProps> {
  return BlockVisualRegistry[type] ?? UnknownBlockVisual
}
